import { faCircleExclamation, faSpinner } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useDispatch, useSelector } from 'react-redux'
import { DateTime } from 'luxon'

import { useOnlineStatus } from '../Hooks/useOnlineStatus'
import { useMutationQueue } from '../../offline/useMutationQueue'
import { useSyncProgress } from '../../offline/syncStatus'
import { RootState } from '../../state/store'
import { setSelectedDate, setSelectedShift } from '../../state/reducers/shiftReducer'

const OfflineBanner = () => {
  const isOnline = useOnlineStatus()
  const { pendingCount } = useMutationQueue()
  const { isSyncing, total, done } = useSyncProgress()
  const dispatch = useDispatch()
  const selectedDate = useSelector((state: RootState) => state.shift.selectedDate)
  const selectedShift = useSelector((state: RootState) => state.shift.selectedShift)
  const provisionalShiftDate = useSelector((state: RootState) => state.shift.provisionalShiftDate)
  const provisionalShift = useSelector((state: RootState) => state.shift.provisionalShift)

  // Note: while the queue is going up, say so - reconnecting after a shift offline takes a while, and
  // an empty screen during it read as the work having been lost.
  // Note: the whole queue goes up as one request, so there is no honest per-item progress to show; it
  // counts what the server has acknowledged, which for a single batch means it jumps from nothing to
  // done. Claiming "1 of 117" while nothing had been confirmed was worse than not counting at all.
  // Note: the sync worker lives in App, so it keeps running as the crew moves between pages. Only
  // closing or reloading interrupts it - and even then the queue survives and goes up on the next run.
  if (isSyncing) {
    return (
      <div className="w-full flex flex-nowrap items-center justify-center gap-2 px-4 py-2 short:py-1 bg-theme-info text-theme-info-muted text-sm font-medium border-b border-theme-info-hover animate-slide-in-down shrink-0">
        <FontAwesomeIcon icon={faSpinner} spin className="shrink-0" />
        <span>
          {done > 0 && done < total
            ? `Syncing your offline changes - ${done} of ${total} done.`
            : `Syncing ${total} offline ${total === 1 ? 'change' : 'changes'}.`}
          {' '}You can carry on using Eclipse - just don't close the browser until it finishes.
        </span>
      </div>
    )
  }

  // Note: online with a queue still to go - the sync runs on reconnect and on a timer, so between
  // attempts there is nothing else to tell the crew their work is still waiting rather than lost.
  if (isOnline && pendingCount > 0) {
    return (
      <div className="w-full flex flex-nowrap items-center justify-center gap-2 px-4 py-2 short:py-1 bg-theme-info text-theme-info-muted text-sm font-medium border-b border-theme-info-hover animate-slide-in-down shrink-0">
        <FontAwesomeIcon icon={faCircleExclamation} className="shrink-0" />
        <span>{pendingCount} offline {pendingCount === 1 ? 'change is' : 'changes are'} waiting to sync.</span>
      </div>
    )
  }

  if (isOnline) return null

  // Note: a shift created offline is the only shift that can be worked, and it is easy to navigate
  // away from it - the crumbs happily move to a shift with no cached data and no way to complete one.
  // Offer the way back, since nothing else on screen says which shift it was.
  const isOnProvisionalShift = provisionalShiftDate === selectedDate && provisionalShift === selectedShift
  const showReturnLink = !!provisionalShiftDate && !!provisionalShift && !isOnProvisionalShift
  const returnToProvisionalShift = () => {
    if (!provisionalShiftDate || !provisionalShift) return
    dispatch(setSelectedDate(provisionalShiftDate))
    dispatch(setSelectedShift(provisionalShift))
  }

  return (
    <div className="w-full flex flex-nowrap items-center justify-center gap-2 px-4 py-2 short:py-1 bg-theme-warning text-theme-primary-contrast text-sm font-medium border-b border-theme-warning-hover animate-slide-in-down shrink-0">
      <FontAwesomeIcon icon={faCircleExclamation} className="shrink-0" />
      <span className="hidden sm:inline">You're offline — edits are queued and will sync when you reconnect</span>
      <span className="sm:hidden">Offline — edits are queued</span>
      {pendingCount > 0 && (
        <span className="ml-1 px-2 py-0.5 rounded-full bg-white/25 text-xs font-bold shrink-0">
          {pendingCount} pending
        </span>
      )}
      {showReturnLink && (
        <button
          type="button"
          onClick={returnToProvisionalShift}
          className="ml-2 px-2 py-0.5 rounded-sm bg-white/25 hover:bg-white/40 text-xs font-bold shrink-0 cursor-pointer transition"
        >
          Back to {DateTime.fromISO(provisionalShiftDate!).toFormat('dd MMM')} {provisionalShift === 'DAY' ? 'day' : 'night'} shift
        </button>
      )}
    </div>
  )
}

export default OfflineBanner
